
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from './ToastContext';
import { useAuth } from './auth';
import { userProfileService } from '../domain/services/userProfileService';

interface FavoritesContextType {
  favorites: string[]; // Store Product IDs
  toggleFavorite: (productId: string) => void;
  isFavorite: (productId: string) => boolean;
}

const FavoritesContext = createContext<FavoritesContextType | null>(null);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<string[]>([]);
  const { addToast } = useToast();
  const { user } = useAuth();

  // Load favorites when user changes (Login/Logout)
  useEffect(() => {
      if (user) {
          userProfileService.getFavorites(user.id).then(setFavorites);
      } else {
          // Guest favorites could be handled here or cleared
          setFavorites([]);
      }
  }, [user]);

  const toggleFavorite = useCallback(async (productId: string) => {
    if (!user) {
        addToast("Inicia sesión para guardar favoritos", "warning");
        return;
    }

    // Optimistic Update
    const isCurrentlyFav = favorites.includes(productId);
    const newFavorites = isCurrentlyFav 
        ? favorites.filter(id => id !== productId) 
        : [...favorites, productId];
    
    setFavorites(newFavorites);
    addToast(isCurrentlyFav ? "Eliminado de favoritos" : "Agregado a favoritos", isCurrentlyFav ? "info" : "success");

    // Background Sync
    await userProfileService.toggleFavorite(user.id, productId);
  }, [user, favorites, addToast]);

  const isFavorite = useCallback((productId: string) => favorites.includes(productId), [favorites]);

  const value = useMemo(() => ({
    favorites,
    toggleFavorite,
    isFavorite
  }), [favorites, toggleFavorite, isFavorite]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) throw new Error("useFavorites must be used within FavoritesProvider");
  return context;
};
